import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, Clock, AlertCircle, Edit, Star } from 'lucide-react';

const SupervisorDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'My Supervisor Dashboard | I Need A Social Worker';
    axios.get(`${API_URL}/supervisor/my-profile`).then((r) => setProfile(r.data.profile)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API_URL}/supervisor/my-stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20">Loading…</div>;

  const status = profile?.verification_status;
  const published = profile?.is_published;
  const membership = profile?.membership_status;
  const acceptingNew = profile?.accepting_new !== false;

  const toggleAvailability = async () => {
    const next = !acceptingNew;
    try {
      await axios.post(`${API_URL}/supervisor/availability`, { accepting_new: next });
      setProfile((p) => ({ ...p, accepting_new: next, is_currently_featured: next ? p.is_currently_featured : false }));
      toast.success(next ? 'You are now shown as accepting new supervisees.' : 'Marked as not accepting new supervisees. You will not appear in the Featured section.');
    } catch (e) { toast.error('Could not update availability.'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-3xl font-bold text-primary mb-6">Supervisor Dashboard</h1>

        {!profile && (
          <div className="bg-white rounded-2xl border p-8 text-center" data-testid="no-profile">
            <p className="text-gray-600 mb-4">You haven't created a supervisor listing yet.</p>
            <Link to="/become-a-supervisor"><Button className="bg-secondary text-white" data-testid="create-listing">Create My Listing</Button></Link>
          </div>
        )}

        {profile && (
          <div className="space-y-5">
            {/* Status cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatusCard
                icon={status === 'verified' ? CheckCircle2 : status === 'rejected' ? AlertCircle : Clock}
                color={status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'amber'}
                title="Verification"
                value={status === 'verified' ? 'Verified' : status === 'rejected' ? 'Not approved' : 'Pending review'}
                desc={status === 'verified' ? 'Your credentials are verified.' : status === 'rejected' ? 'Please update your details and resubmit.' : 'Our team is reviewing your credentials.'}
                testid="verification-status"
              />
              <StatusCard
                icon={membership === 'active' ? CheckCircle2 : AlertCircle}
                color={membership === 'active' ? 'green' : 'amber'}
                title="Membership"
                value={membership === 'active' ? (profile.plan_id === 'annual' ? 'Annual' : profile.plan_id === 'featured' ? 'Featured' : 'Basic') : 'Inactive'}
                desc={membership === 'active' ? `Renews / expires ${(profile.membership_expires || '').slice(0, 10)}` : 'Activate a plan to publish your listing.'}
                testid="membership-status"
              />
            </div>

            {/* Profile analytics — homepage feature performance + profile views */}
            {stats && (
              <div className="bg-white rounded-2xl border p-5" data-testid="supervisor-analytics">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Star className="w-5 h-5 text-secondary" /> Your Profile Performance</h2>
                  {stats.homepage_featured && <Badge className="bg-secondary/10 text-secondary text-xs">Featured on homepage</Badge>}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-primary/5 p-4 text-center" data-testid="stat-profile-views">
                    <p className="text-3xl font-bold text-primary tabular-nums">{stats.profile_views}</p>
                    <p className="text-xs text-gray-500 mt-1">Profile views</p>
                  </div>
                  <div className="rounded-xl bg-primary/5 p-4 text-center" data-testid="stat-homepage-impressions">
                    <p className="text-3xl font-bold text-primary tabular-nums">{stats.homepage_impressions}</p>
                    <p className="text-xs text-gray-500 mt-1">Homepage feature views</p>
                  </div>
                  <div className="rounded-xl bg-primary/5 p-4 text-center" data-testid="stat-homepage-clicks">
                    <p className="text-3xl font-bold text-primary tabular-nums">{stats.homepage_clicks}</p>
                    <p className="text-xs text-gray-500 mt-1">Homepage clicks</p>
                  </div>
                </div>
                {stats.verification_status === 'verified' && !stats.homepage_featured && (
                  <p className="text-xs text-gray-500 mt-3">Verified supervisors are rotated into the homepage "Featured Verified Supervisors" section automatically.</p>
                )}
              </div>
            )}

            <div className={`rounded-2xl border p-5 ${published ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="listing-live-banner">
              <p className={`font-semibold ${published ? 'text-green-800' : 'text-amber-800'}`}>
                {published ? '✓ Your listing is LIVE in the supervisor directory' : 'Your listing is not live yet'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {published ? 'Social workers can find and contact you now.' : 'Listings go live once your credentials are verified AND a membership is active.'}
              </p>
            </div>

            {/* Availability toggle — supervisor controls whether they're accepting supervisees */}
            <div className="bg-white rounded-2xl border p-5 flex items-center justify-between gap-4" data-testid="availability-card">
              <div>
                <p className="font-semibold text-primary">Accepting new supervisees</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  {acceptingNew
                    ? 'You are visible in the directory and eligible for the Featured section.'
                    : 'You are marked as not currently accepting supervisees and are removed from the Featured section (your profile stays in the directory).'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={acceptingNew}
                onClick={toggleAvailability}
                data-testid="availability-toggle"
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${acceptingNew ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${acceptingNew ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Profile summary */}
            <div className="bg-white rounded-2xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl font-bold text-primary flex items-center gap-2">
                  {profile.full_name}
                  {profile.is_featured && <Badge className="bg-secondary text-white text-xs flex items-center gap-1"><Star className="w-3 h-3" /> Featured</Badge>}
                </h2>
                <Link to="/become-a-supervisor"><Button variant="outline" size="sm" className="flex items-center gap-1"><Edit className="w-4 h-4" /> Edit</Button></Link>
              </div>
              <p className="text-sm text-gray-600">{profile.license_type} • {profile.city ? `${profile.city}, ` : ''}{profile.license_state}</p>
              <p className="text-gray-700 mt-3 text-sm">{profile.bio}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(profile.specialties || []).map((s) => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
              </div>
              {published && (
                <Link to={`/supervisors/${profile.user_id}`} className="inline-block mt-4">
                  <Button variant="outline" size="sm">View Public Profile</Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusCard = ({ icon: Icon, color, title, value, desc, testid }) => {
  const colors = {
    green: 'text-green-600 bg-green-100', amber: 'text-amber-600 bg-amber-100', red: 'text-red-600 bg-red-100',
  };
  return (
    <div className="bg-white rounded-2xl border p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-xs text-gray-400">{title}</p>
          <p className="font-bold text-primary">{value}</p>
        </div>
      </div>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );
};

export default SupervisorDashboard;
